// ── Chat domain types ─────────────────────────────

export interface ToolCall {
  tool: string;
  input: Record<string, unknown>;
  result?: string;
  duration_ms?: number;
}

export interface TokenBreakdown {
  system_prompt: number;
  tool_schemas: number;
  memory: number;
  history: number;
  message: number;
}

export interface UsageInfo {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_tokens?: number;
  cache_read_tokens?: number;
  cost_usd?: number;
  cost_without_cache?: number;
  iterations?: number;
  tools_loaded?: number;
  routed_groups?: string[];
  token_breakdown?: TokenBreakdown;
  /** Resolved model name from the backend, e.g. "gpt-5-mini". */
  model?: string;
  /** LLM provider, e.g. "openai" / "anthropic". */
  provider?: string;
}

export interface PrewarmCandidate {
  id: string | number;
  label: string;
  entity: string;
  hint: string;
  score?: number;
  tier?: string;
  applies_to?: string;
  filter_field?: string;
  filter_syntax?: string;
}

export interface PrewarmHint {
  mention: string;
  candidates: PrewarmCandidate[];
}

export interface ChatResponse {
  response: string;
  tool_calls: ToolCall[];
  usage?: UsageInfo;
}

// SSE event types from /api/chat/stream
export type SSEEvent =
  | { type: "status"; text: string }
  | {
      type: "intent";
      intents: string[];
      entities?: string[];
      display?: boolean;
      pruned?: number;
    }
  | {
      type: "router";
      groups: string[];
      tools_loaded: number;
      total_tools: number;
      cost?: number;
      enriched_query?: string;
      backrefs?: Record<string, string>;
      carried?: number;
      auto_chain?: boolean;
    }
  | { type: "thinking"; duration_ms?: number; text?: string }
  | {
      type: "tool_call";
      tool: string;
      input: Record<string, unknown>;
      result?: string;
      duration_ms?: number;
      declined?: boolean;
    }
  | {
      type: "confirm_write";
      confirm_id: string;
      tools: { tool: string; input: Record<string, unknown> }[];
      read_tools?: string[];
      reasoning?: string;
    }
  | { type: "memory"; memory: Record<string, unknown> }
  | {
      type: "memory_recall";
      memories: MemoryRecallItem[];
      context_snippet?: string;  // full assembled context injected into planner
      duration_ms?: number;
    }
  | {
      type: "pipeline_trace";
      engine?: string;
      intent?: Record<string, unknown>;
      fql?: Record<string, unknown>;
      capability?: Record<string, unknown>;
      final_requests?: Array<{
        method?: string;
        path?: string;
        body?: Record<string, unknown>;
        query_params?: Record<string, unknown>;
        missing_fields?: string[];
        response?: unknown;
      }>;
    }
  | { type: "prewarm"; summary?: string; hints: PrewarmHint[] }
  // F12 small-LLM noise filter — decisions log on the Tier-3 entity-noun
  // candidate set. `kept` / `dropped` are dicts keyed by noun, values are
  // entity_id arrays. Surfaced in the devbar so Owen can spot-check
  // F12's small-LLM decisions in real time.
  | {
      type: "prewarm_filter";
      status?: "applied" | "skipped_low_confidence" | "skipped_no_key"
              | "skipped_no_hits" | string;
      kept?: Record<string, string[]>;
      dropped?: Record<string, string[]>;
      confidence?: "high" | "med" | "low";
      removed?: string[];
    }
  // F13 — planner-side small-LLM filter on the memory similarity_recall
  // bucket. Sibling of prewarm_filter. always_count / scope_count are
  // the sacred buckets that bypass the filter; recall_in / recall_kept
  // are the input/output for the bucket that DOES go through the LLM.
  | {
      type: "memory_filter";
      status?: "applied" | "applied_cached" | "skipped_no_items"
              | "skipped_low_confidence" | "skipped_no_key"
              | "skipped_too_large" | "timeout" | "error" | string;
      confidence?: "high" | "med" | "low";
      always_count?: number;
      scope_count?: number;
      recall_in?: number;
      recall_kept?: number;
      // Deterministic-drop bucket: recent_event items with
      // direction=outbound (prior assistant responses with
      // potentially-stale CRM data). Never reach the small-LLM
      // filter, never enter the knowledge block.
      outbound_dropped_count?: number;
      kept_count?: number;
      dropped_count?: number;
      duration_ms?: number;
      error?: string;
    }
  | { type: "export"; url: string; filename?: string }
  | { type: "system_prompt"; text: string }
  | {
      type: "http_call";
      op_id?: string | null;
      method?: string;
      url?: string | null;
      status?: number | null;
      error?: string | null;
    }
  | {
      type: "done";
      response: string;
      tool_calls: ToolCall[];
      usage: UsageInfo;
      memory?: Record<string, unknown>;
    }
  | { type: "error"; response: string }
  // ── Topic tracking Part 5 (Owen + bug-diagnoser 2026-05-28) ────
  // Per-turn snapshot the DevPanel Conversation Understander block
  // consumes. Sticky — useChatManager keeps only the latest payload
  // on `activeSession.topicUnderstanding`. Spec:
  // planner-manager/public/ui-dev-conversation-understander-block-2026-05-28.md
  | {
      type: "topic_understanding";
      goal: string | null;
      active_entity: {
        type: string;
        id: number | null;
        label: string | null;
        confidence: "high" | "med" | "low";
        source: string;
      } | null;
      topic_intent: string | null;
      pinned_path: string | null;
      prewarm_actions?: string[];
      history_pair_count?: number;
    }
  // Pinned path event — carries the canonical Tier-3 path + chain
  // depth for the Prewarm row's "3-step chain hint loaded" suffix.
  | {
      type: "topic_path_pinned";
      entity_type?: string;
      topic_intent?: string;
      canonical_path?: string;
      chain_summary?: Array<Record<string, unknown>>;
      chain_steps?: number;
      has_chain?: boolean;
      has_create_topology?: boolean;
    }
  // Active entity transition signal. The canonical state lives in
  // topic_understanding; this event is for trace correlation.
  | {
      type: "active_entity_changed";
      prior: Record<string, unknown> | null;
      current: Record<string, unknown> | null;
    }
  // v2 Part 2 — entities transition signal.
  | {
      type: "entities_changed";
      prior: Array<Record<string, unknown>>;
      current: Array<Record<string, unknown>>;
    }
  // v2 Part 3 — prewarm_v2 chain start envelope.
  | {
      type: "prewarm_v2_start";
      entity_count: number;
      to_resolve_count: number;
    }
  // v2 Part 3 — prewarm_v2 chain done envelope.
  | {
      type: "prewarm_v2_done";
      action_count: number;
      total_duration_ms: number;
    }
  // v2 Part 3 — per-prefetch action event. Devbar Part 6
  // PROACTIVELY LOADED panel consumes these.
  | {
      type: "prewarm_action";
      action: string;
      target: string;
      duration_ms: number;
      ok: boolean;
      bytes: number;
      cache_hit: boolean;
      error: string | null;
    }
  // ── New planner event vocab (Q3 / Q4 / A1 / A2) ────────────────────
  // A2 — runtime-derived label between LLM calls. Drives the in-chat
  // status pill ("inspecting Quotes" / "looking up quote") so the
  // 2-7s LLM-thinking gap is no longer silent.
  | { type: "agent_intent"; label: string; iteration?: number }
  // Fix #0 (bug-diagnoser 2026-05-25). The user's prompt surfaced as
  // a dedicated event so the DevPanel orchestration trace can render
  // it inline alongside the agent's background work — copying the
  // trace yields "user said X → background did Y → agent answered Z".
  | { type: "user_message"; text: string; user_turn?: number; ts_ms?: number }
  // A1 — incremental tokens of the assistant's final text. Frontend
  // accumulates into an in-flight bubble; replaced by the canonical
  // `done.response` on terminal event.
  | { type: "assistant_text_delta"; text: string; iteration?: number; seq?: number }
  // Q4 chain lifecycle. plan_committed lands the plan card; step_*
  // mutate it; chain_aborted/partial surface orphan entities.
  | {
      type: "plan_committed";
      plan_id: string;
      total_steps: number;
      steps: Array<{ step_idx: number; op_id: string; summary?: string;
                      op_human_label?: string }>;
      trigger?: string;
    }
  // v2.2 task 7 — structural body schema per step. Emitted alongside
  // plan_committed (same plan_id). MessageList renders the diagram
  // off this. Body may be empty until task 8 (system-prompt update)
  // ships — diagram falls back to a placeholder row in that window.
  | {
      type: "chain_plan_structured";
      plan_id: string;
      steps: Array<{
        step_idx: number;
        op_id: string;
        op_human_label: string;
        is_new: boolean;
        body: Record<string, {
          value: unknown;
          source:
            | "from_email"
            | "from_user_input"
            | "resolved"
            | "from_step"
            | "from_handle_context"
            | "inferred_from_context";
          from_step_idx?: number;
          from_field?: string;
          display?: string;
        }>;
        fk_inputs: Array<{
          field: string;
          fk_entity: string | null;
          allowed_values: Array<{ id: number; name: string }>;
          required: boolean;
          submitted_value: number | string | null;
        }>;
        prereq_questions: Array<{ field: string; prompt: string }>;
      }>;
    }
  | {
      type: "step_started";
      plan_id: string;
      step_idx: number;
      total_steps: number;
      op_id: string;
      summary?: string;
      op_human_label?: string;
    }
  | {
      type: "step_completed";
      plan_id: string;
      step_idx: number;
      total_steps: number;
      op_id: string;
      result_summary?: string;
      new_handle_id?: string | null;
      new_entity_id?: number | string | null;
      duration_ms?: number;
    }
  | {
      type: "step_failed";
      plan_id: string;
      step_idx: number;
      total_steps: number;
      op_id: string;
      reasons?: Array<{ message?: string; path?: string; code?: string }>;
      recoverable?: boolean;
    }
  | {
      type: "step_skipped";
      plan_id: string;
      step_idx: number;
      total_steps: number;
      op_id: string;
      reason?: string;
    }
  | {
      type: "step_retrying";
      plan_id: string;
      step_idx: number;
      total_steps: number;
      op_id: string;
      attempt?: number;
      max_attempts?: number;
      reason?: string;
    }
  | {
      type: "chain_aborted";
      plan_id?: string | null;
      reason: "user_stop" | "fatal_error" | string;
      completed_steps: number;
      partial_handles: Array<{
        step: number; op_id: string;
        handle_id?: string | null; new_id?: number | string | null;
      }>;
    }
  | {
      type: "chain_partial";
      plan_id?: string;
      failed_at_step?: number;
      completed_steps?: number;
      total_steps_in_chain?: number;
      partial_handles?: Array<{
        step: number; op_id: string;
        handle_id?: string | null; new_id?: number | string | null;
      }>;
      reasons?: Array<{ class?: string; message?: string; path?: string }>;
    }
  | { type: "chain_paused"; plan_id: string; reason?: string;
      completed_steps?: number; awaiting_user_input?: boolean }
  | { type: "chain_resumed"; plan_id: string; resumed_at_step?: number }
  // Q3 — fires when a plan-required threshold is met without a prior
  // plan_chain call. v1 lenient: warning only, dispatch still runs.
  | {
      type: "plan_missing";
      user_turn?: number;
      trigger: "N_ge_3" | "fanout_gt_10" | "write_after_write" | string;
      first_op_id?: string | null;
      warning?: string;
    }
  // Tier 5/6 hit emit — surfaces deterministic attribute/metric
  // prewarm results so the DevPanel can show what the agent saw.
  // `tier` discriminates which tier; `hits` is {word: [hit_dict, ...]}.
  // The hit_dict shape varies by tier (attribute vs metric path) but
  // both carry entity_id + word at minimum.
  | {
      type: "tier_hit";
      tier: "tier5_attribute" | "tier6_metric" | string;
      hits: Record<string, Array<{
        word: string;
        entity_id: string;
        // Tier 5 fields
        field_name?: string;
        field_kind?: string;
        fk_targets?: string[];
        filter_path?: string;
        // Tier 6 fields
        path?: string;
        description?: string;
        // Composition (both tiers)
        composition?: {
          mention?: string;
          id?: number | string;
          label?: string;
          entity?: string;
        };
      }>>;
      user_turn?: number;
    }
  // Memory Manager — explicit save_fact tool fired mid-turn. Backend
  // emits one of these per successful save; chat surface renders an
  // inline "Remembered: …" note. `fact_pending_confirmation` is the
  // soft variant (writer flagged it for user confirmation before the
  // fact becomes live); `fact_saved` is the green-light variant.
  | {
      type: "fact_saved" | "fact_pending_confirmation";
      fact_id?: string;
      fact_type?: "preference" | "constraint" | "fact" | "role" | "relationship";
      scope?: { type?: string; id?: string | number };
      human_readable?: string;
      status?: "new" | "confirmed" | "superseded";
    }
  // Owen 2026-05-26 — full LLM input snapshot per iteration. Used by
  // the DevPanel `llm_input` node. Backend snake_case field names
  // mirrored verbatim from the SSE payload.
  | {
      type: "llm_input";
      iteration?: number;
      system_prompt?: string;
      messages?: Array<Record<string, unknown>>;
      tool_names?: string[];
      system_prompt_chars?: number;
      messages_chars?: number;
      ts_ms?: number;
    }
  // ── Goal/Plan primitive (Phase C — planner-manager 2026-05-26) ──
  // Eight events drive <GoalBanner /> + <DraftActionPlanCard /> +
  // the "Plan for: …" header on the existing chain-plan card. All
  // forward through the planner_agent adapter + main.py SSE allowlist.
  | {
      type: "goal_set";
      goal: string;
      set_via: "user_initial" | string;
    }
  | {
      type: "goal_revised";
      goal: string;
      prev_goal: string;
      set_via: "user_correction" | "agent_proposed_user_confirmed" | string;
    }
  | {
      type: "goal_change_proposed";
      proposed_goal: string;
      current_goal: string;
      reason: string;
    }
  | {
      type: "goal_change_accepted";
      new_goal: string;
      prev_goal: string;
    }
  | {
      type: "goal_change_rejected";
      proposed_goal: string;
      current_goal: string;
    }
  | {
      type: "draft_action_plan";
      name: string;
      description?: string;
      when_plain_english: string;
      then_steps: string[];
      notifications_plain_english?: string;
      manual_confirmation_required: boolean;
    }
  | {
      type: "plan_linked_to_goal";
      plan_id: string;
      goal: string;
    }
  | {
      type: "plan_unlinked";
      plan_id: string;
      goal: string;
      outcome: string;
    }
  // Topic-tracking events (Planner Manager Parts 1-2, 2026-05-28).
  // Shapes are read defensively in useChatManager.ts via unknown casts,
  // so the union members below only need the discriminant + minimal
  // fields — the type-system contract is the `type` string itself.
  | {
      type: "topic_understanding";
      goal?: string | null;
      active_entity?: unknown;
      topic_intent?: unknown;
      pinned_path?: string | null;
      prewarm_actions?: string[];
      history_pair_count?: number;
    }
  | {
      type: "topic_path_pinned";
      chain_summary?: unknown[];
      chain_steps?: number;
      has_chain?: boolean;
    }
  | {
      type: "active_entity_changed";
    }
  // Planner Manager Parts 3-4 (2026-05-30). Shape read defensively in
  // useChatManager.ts via unknown casts.
  | {
      type: "llm_classifier";
    }
  | {
      type: "convogen_call";
    };

// ── Goal banner state ─────────────────────────────────────────────
// Tracked per ChatSession in useChatManager. Reset on session change.

export interface GoalHistoryEntry {
  goal: string;
  setAt: string;          // ISO timestamp
  setVia: string;         // raw set_via from backend
  prevGoal: string | null;
}

export interface PendingGoalChange {
  proposedGoal: string;
  currentGoal: string;
  reason: string;
  proposedAt: string;     // ISO timestamp
}

export interface GoalState {
  primaryGoal: string | null;
  goalHistory: GoalHistoryEntry[];
  pendingChange: PendingGoalChange | null;
}

export const EMPTY_GOAL_STATE: GoalState = {
  primaryGoal: null,
  goalHistory: [],
  pendingChange: null,
};

// ── Draft action plan card payload ────────────────────────────────
// Stamped onto the assistant ChatMessage that arrived on the same
// turn (linked via the `done` handler in useChatManager).

export interface DraftActionPlan {
  id: string;
  name: string;
  description?: string;
  whenPlainEnglish: string;
  thenSteps: string[];
  notificationsPlainEnglish?: string;
  manualConfirmationRequired: boolean;
}

// Memory recall items returned by the arbitration engine
export interface MemoryRecallItem {
  layer:
    | "long_term"
    | "working"
    | "business_fact"
    | "memory_chunk"
    | "active_context"
    | "task_log"
    | "candidate"
    | "daily_log"
    | "canonical_schema";
  category?: string;
  key?: string;
  value?: string;
  confidence?: number;
  content?: string;
  log_date?: string;
  channel?: string;
  source_type?: string;
  subject?: string;
  entity_type?: string;
  action?: string;
  status?: string;
  name?: string;
}

// ── Chain plan (v2.2 task 9) ───────────────────────
// User-facing structural view of a committed plan_chain. The agent
// emits `chain_plan_structured` alongside `plan_committed` with the
// per-step body shape (field values + sources, FK enums, prereqs).
// MessageList renders <ChainPlanDiagram /> next to the assistant
// message whose chainPlan slot is populated.

export type ChainPlanFieldSource =
  | "from_email"
  | "from_user_input"
  | "resolved"
  | "from_step"
  | "from_handle_context"
  | "inferred_from_context";

export interface ChainPlanField {
  value: unknown;
  source: ChainPlanFieldSource;
  // Populated when source === "from_step"
  from_step_idx?: number;
  from_field?: string;
  // Populated when source === "resolved" — human-readable label to
  // render in place of the raw ID (e.g. "Bield" not 5).
  display?: string;
}

export interface ChainPlanFkInput {
  field: string;
  fk_entity: string | null;
  allowed_values: Array<{ id: number; name: string }>;
  required: boolean;
  submitted_value: number | string | null;
}

export interface ChainPlanPrereqQuestion {
  field: string;
  prompt: string;
}

export interface ChainPlanStepState {
  stepIdx: number;
  opId: string;
  opHumanLabel: string;
  isNew: boolean;
  body: Record<string, ChainPlanField>;
  fkInputs: ChainPlanFkInput[];
  prereqQuestions: ChainPlanPrereqQuestion[];
  status: "pending" | "running" | "done" | "failed" | "skipped";
  failureText?: string;
}

export interface ChainPlanState {
  planId: string;
  steps: ChainPlanStepState[];
  submitted: boolean;
  aborted?: boolean;
  abortReason?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "proactive";
  content: string;
  toolCalls?: string[]; // tool names used
  jobName?: string;
  /** Set when the assistant called `propose_action_plan` on this turn.
   *  Message component renders <DraftActionPlanCard /> above the bubble. */
  draftPlan?: DraftActionPlan;
  /** Set when the agent committed a `plan_chain` on this turn.
   *  MessageList renders <ChainPlanDiagram /> next to the bubble.
   *  step_* SSE events mutate `steps[].status` in place after approve. */
  chainPlan?: ChainPlanState;
}
